import { buildApiUrl } from '@map/hooks/baseApi';
import { UserLanguage, WithCancelToken } from '@map/types';
import useAction from '@shared/hooks/useAction';
import { keyBy } from '@shared/util/array';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Dispatch, Reducer } from 'redux';
import { createSelector } from 'reselect';

/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
const USER_LANGUAGES_FETCHED = 'USER_LANGUAGES_FETCHED' as const;
const USER_LANGUAGE_CREATED = 'USER_LANGUAGE_CREATED' as const;
const USER_LANGUAGE_UPDATED = 'USER_LANGUAGE_UPDATED' as const;
const USER_LANGUAGE_DESTROYED = 'USER_LANGUAGE_DESTROYED' as const;
/* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */

const actionCreators = {
  userLanguagesFetched: (data: UserLanguage[]) =>
    ({
      type: USER_LANGUAGES_FETCHED,
      data,
    }) as const,
  userLanguageCreated: (data: UserLanguage) =>
    ({
      type: USER_LANGUAGE_CREATED,
      data,
    }) as const,
  userLanguageUpdated: (data: UserLanguage) =>
    ({
      type: USER_LANGUAGE_UPDATED,
      data,
    }) as const,
  userLanguageDestroyed: (data: Pick<UserLanguage, 'id'>) =>
    ({
      type: USER_LANGUAGE_DESTROYED,
      data,
    }) as const,
};

type State = Record<number, UserLanguage>;
type UserLanguagesAction = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const userLanguages: Reducer<State, UserLanguagesAction> = (state = {}, action) => {
  switch (action.type) {
    case USER_LANGUAGES_FETCHED:
      return keyBy(action.data, ({ id }) => id);
    case USER_LANGUAGE_CREATED:
    case USER_LANGUAGE_UPDATED:
      return { ...state, [action.data.id]: action.data };
    case USER_LANGUAGE_DESTROYED: {
      const { [action.data.id]: _ignored, ...newState } = state;
      return newState;
    }
    default:
      return state;
  }
};

export default userLanguages;

// SELECTORS

const userLanguagesSelector = (rootState: { userLanguages: State }) => rootState.userLanguages;

export const userLanguageValuesSelector = createSelector(userLanguagesSelector, (hh) =>
  Object.values(hh).sort((a, b) => a.language.localeCompare(b.language)),
);
export const useUserLanguages = () => useSelector(userLanguageValuesSelector);

export const userLanguagesExistSelector = createSelector(
  userLanguagesSelector,
  (languages) => Object.values(languages).length > 0,
);

const { userLanguageCreated, userLanguageDestroyed, userLanguageUpdated, userLanguagesFetched } =
  actionCreators;

export const fetchUserLanguages =
  ({ cancelToken }: WithCancelToken) =>
  async (dispatch: Dispatch) => {
    const { data } = await axios.get(buildApiUrl('user_languages'), { cancelToken });
    const userLanguages = data.languages as UserLanguage[];
    return dispatch(userLanguagesFetched(userLanguages));
  };

const createUserLanguage = (language: Omit<UserLanguage, 'id'>) => async (dispatch: Dispatch) => {
  const { data } = await axios.post(buildApiUrl('user_languages'), { language });
  const userLanguage = data.language as UserLanguage;
  return dispatch(userLanguageCreated(userLanguage));
};

export const useCreateUserLanguage = () => useAction(createUserLanguage);

const updateUserLanguage = (language: UserLanguage) => async (dispatch: Dispatch) => {
  const { data } = await axios.put(buildApiUrl(`user_languages/${language.id}`), { language });
  const userLanguage = data.language as UserLanguage;
  return dispatch(userLanguageUpdated(userLanguage));
};

export const useUpdateUserLanguage = () => useAction(updateUserLanguage);

const destroyUserLanguage =
  ({ id }: Pick<UserLanguage, 'id'>) =>
  async (dispatch: Dispatch) => {
    const { data } = await axios.delete(buildApiUrl(`user_languages/${id}`));
    const userLanguage = data.language as Pick<UserLanguage, 'id'>;
    return dispatch(userLanguageDestroyed(userLanguage));
  };

export const useDestroyUserLanguage = () => useAction(destroyUserLanguage);
